import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * Navigation module
 * - Handelt openen/sluiten van menu op mobile
 * - Regelt eventlisteners voor hamburger toggles, ESC, etc.
 */

let menu;
let siteWrapper;
let resetTimer = null;
// Let op dat deze waarde gesynchroniseerd is met je CSS transitions (.4s)
const TRANSITION_DURATION = 600; 

/**
 * Zorgt dat de site-wrapper weer originele hoogte krijgt
 */
function resetHeight() {
  if (!siteWrapper) return;
  // Verwijder inline style
  siteWrapper.style.height = '';
  window.removeEventListener('resize', updateSiteWrapperHeight);
}

/**
 * Update site-wrapper height bij resizes (voorkom jumpen)
 */
function updateSiteWrapperHeight() {
  if (!siteWrapper) return;
  // Let op: op Safari kunnen addressbar-animaties nog steeds voor
  // regelmatig wisselende window.innerHeight zorgen. Overweeg
  // min-height: 100vh in CSS, om dit in JS te vermijden.
  siteWrapper.style.height = window.innerHeight + 'px';
}

/**
 * openMenu:
 * Adds classes, sets height, etc.
 */
export function openMenu() {
  if (!menu) menu = document.querySelector('#menu-mobile');
  if (!menu) return;

  clearTimeout(resetTimer);

  menu.classList.add('collapsed');
  document.body.classList.add('menu-open');

  // Hoogte van siteWrapper forceren
  updateSiteWrapperHeight();
  window.addEventListener('resize', updateSiteWrapperHeight);

  // Body scroll lock alleen na transitie als je dat wilt:
  // (soms stottert Safari als je meteen body-scroll-lock doet)
  // siteWrapper.addEventListener('transitionend', handleTransitionEnd);
  // function handleTransitionEnd(e) {
  //   if (e.target === siteWrapper && e.propertyName === 'transform') {
  //     siteWrapper.removeEventListener('transitionend', handleTransitionEnd);
  //     disableBodyScroll(menu);
  //   }
  // }
}

/**
 * closeMenu:
 * Removes classes, re-enables scroll, etc.
 */
export function closeMenu() {
  if (!menu) menu = document.querySelector('#menu-mobile');
  if (!menu) return;

  clearTimeout(resetTimer);

  menu.classList.remove('collapsed');
  document.body.classList.remove('menu-open');

  // Scroll lock opheffen
  enableBodyScroll(menu);

  // Na de transitie de hoogte resetten
  resetTimer = setTimeout(resetHeight, TRANSITION_DURATION + 50);
}

/**
 * initNavigation:
 * Set up the togglers, keydown ESC, etc.
 */

export function initNavigation() {

  // Referenties
  menu = document.querySelector('#menu-mobile');
  siteWrapper = document.querySelector('.site-wrapper');
  const togglers = document.querySelectorAll('.hamburger');

  if (!menu || togglers.length === 0 || !siteWrapper) return;

  // Klik toggles
  togglers.forEach((toggler) => {
    toggler.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = menu.classList.contains('collapsed');
      isOpen ? closeMenu() : openMenu();
    });
  });

  // Sluiten via ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('collapsed')) {
      closeMenu();
    }
  });
}

/**
 * navColorContrast.js
 * - Alle contrastberekeningen voor je header
 * - Eenvoudige WCAG check: wit of zwart, net als je had
 */

function getRelativeLuminance(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getContrastRatio(fg, bg) {
  const L1 = getRelativeLuminance(fg[0], fg[1], fg[2]);
  const L2 = getRelativeLuminance(bg[0], bg[1], bg[2]);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

function parseRGB(rgbStr) {
  const match = rgbStr.match(/\d+/g);
  return match ? match.map(Number) : null;
}

function shouldUseDarkText(bgRGB) {
  const contrastWithBlack = getContrastRatio([0, 0, 0], bgRGB);
  const contrastWithWhite = getContrastRatio([255, 255, 255], bgRGB);
  return contrastWithBlack > contrastWithWhite;
}

/**
 * updateNavColor:
 * Checks the element behind the .main-header and applies .is-dark if needed
 */
export function updateNavColor() {
  const nav = document.querySelector('.main-header');
  if (!nav) return;

  const rect = nav.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;

  // Temporarily disable pointer-events
  nav.style.pointerEvents = 'none';
  const behind = document.elementFromPoint(x, y);
  nav.style.pointerEvents = '';

  //const parent = behind?.closest('.component, .main-footer');
  const parent = behind?.closest('.component, .main-footer, #main');
  if (!parent) {
    nav.classList.add('is-dark');
    return;
  }

  const style = getComputedStyle(parent);
  const bg = style.backgroundColor;

  if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') {
    nav.classList.add('is-dark');
    return;
  }

  const rgb = parseRGB(bg);
  if (!rgb) {
    nav.classList.add('is-dark');
    return;
  }

  // Alleen als witte tekst beter is, verwijder je is-dark
  if (!shouldUseDarkText(rgb)) {
    nav.classList.remove('is-dark');
  } else {
    nav.classList.add('is-dark');
  }
}

/**
 * initNavColorContrast:
 * Sets up scroll and resize watchers for the nav color
 */
export function initNavColorContrast() {
  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        updateNavColor();
        ticking = false;
      });
      ticking = true;
    }
  });

  window.addEventListener('DOMContentLoaded', updateNavColor);
  window.addEventListener('resize', updateNavColor);
}













/**
 * Header scroll handler that manages header visibility and styling based on scroll position
 * @param {Object} options - Configuration options
 * @param {string} options.headerSelector - CSS selector for the header element (default: '#site-header')
 * @param {number} options.delta - How far to scroll before showing/hiding navbar (default: 600)
 * @param {number} options.toggleThreshold - Threshold for adding toggleheader class (default: 120)
 * @param {number} options.scrolledThreshold - Threshold for adding headerscrolled class (default: 600)
 * @param {number} options.checkInterval - Interval for checking scroll position in ms (default: 100)
 * @returns {Object} - Methods to start and stop the scroll handler
 */

export function headerScrollHandler(options = {}) {
  // Default options
  const config = {
    headerSelector: '.main-header',
    delta: 600,
    toggleThreshold: 120,
    scrolledThreshold: 600,
    checkInterval: 100,
    ...options
  };

  // Variables
  let didScroll = false;
  let lastScrollTop = 0;
  let intervalId = null;
  
  // Get header element and its height
  const header = document.querySelector(config.headerSelector);
  const navbarHeight = header ? header.offsetHeight : 0;

  // Scroll handler function
  function hasScrolled() {
    const st = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

    // Check if user has scrolled more than delta
    if (Math.abs(lastScrollTop - st) <= config.delta) return;

    // If they scrolled down and are past the navbar, add class .nav-up.
    if (st > lastScrollTop && st > navbarHeight) {
      // Scroll Down
      header.classList.remove('nav-down');
      header.classList.add('nav-up');
    } else {
      // Scroll Up
      if (st + window.innerHeight < document.documentElement.scrollHeight) {
        header.classList.remove('nav-up');
        header.classList.add('nav-down');
      }
    }

    // Add/remove headerscrolled class
    if (st > config.scrolledThreshold) {
      header.classList.add('headerscrolled');
    } else {
      header.classList.remove('headerscrolled');
    }

    // Add/remove toggleheader class
    if (st > config.toggleThreshold) {
      header.classList.add('toggleheader');
    } else {
      header.classList.remove('toggleheader');
    }

    lastScrollTop = st;
  }

  // Event listener for scroll
  function onScroll() {
    didScroll = true;
  }

  // Start the scroll handler
  function start() {
    if (!header) {
      console.error(`Element with selector "${config.headerSelector}" not found`);
      return;
    }

    // Add scroll event listener
    window.addEventListener('scroll', onScroll);

    // Set interval to check if user has scrolled
    intervalId = setInterval(() => {
      if (didScroll) {
        hasScrolled();
        didScroll = false;
      }
    }, config.checkInterval);

    // Initialize header state
    hasScrolled();

    return this;
  }

  // Stop the scroll handler
  function stop() {
    window.removeEventListener('scroll', onScroll);
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    return this;
  }

  // Return public methods
  return {
    start,
    stop
  };
}